// DeletePage.js - Delete Profile Page
import { Button, Popconfirm, Typography, message, Card, Alert } from 'antd';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { useDispatch } from 'react-redux';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { deleteStaff } from '../../redux/rtk/features/user/userSlice';
import getUserFromToken from '../../utils/getUserFromToken';

const { Title, Paragraph } = Typography;

const DeleteProfile = () => {
    const [loader, setLoader] = useState(false);
    const navigate = useNavigate();
    const id = getUserFromToken();
    const dispatch = useDispatch();
    const isLoggedIn = Boolean(localStorage.getItem('isLogged'));

    const onDelete = async () => {
        if (!isLoggedIn || !id) {
            toast.error("Please log in to delete your profile");
            navigate('/admin/auth/login');
            return;
        }

        setLoader(true);
        try {
            const response = await dispatch(deleteStaff(id));
            if (response.payload.message === "success") {
                toast.success("Profile successfully deleted");
                setLoader(false);
                // Clear local storage and redirect
                localStorage.clear();
                navigate('/');
            } else {
                toast.error("Failed to delete profile");
                setLoader(false);
            }
        } catch (error) {
            toast.error("Failed to delete profile");
            console.log("not deleted", error);
            setLoader(false);
        }
    };

    const onCancel = () => {
        message.info("Profile deletion canceled");
    };

    return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
            <Card>
                {!isLoggedIn ? (
                    <Alert
                        message="Login Required"
                        description="You must be logged in to delete your profile. Please log in first."
                        type="warning"
                        showIcon
                        action={
                            <Button size="small" type="primary" onClick={() => navigate('/admin/auth/login')}>
                                Go to Login
                            </Button>
                        }
                    />
                ) : (
                    <>
                        <Title level={2}>Important Notice: Profile Deletion</Title>
                        
                        <Alert
                            message="Warning: Irreversible Action"
                            description="Deleting your profile is a permanent and irreversible action. Please read the information below carefully before proceeding."
                            type="error"
                            icon={<ExclamationCircleOutlined />}
                            showIcon
                            style={{ marginBottom: '24px' }}
                        />

                        <Paragraph>
                            Deleting your profile is a <strong>serious</strong> and <strong>irreversible</strong> action. By proceeding, you will permanently lose access to all of your personal data, including but not limited to:
                        </Paragraph>
                        <Paragraph>
                            <ul>
                                <li>All account details and associated information.</li>
                                <li>All your preferences, settings, and saved configurations.</li>
                                <li>Access to any personalized services, applications, and subscriptions linked to this profile.</li>
                                <li>All stored data such as documents, media, and messages.</li>
                                <li>Historical records, activity logs, and any contributions made within the platform.</li>
                                <li>Attendance records, leave applications, and payroll information.</li>
                            </ul>
                        </Paragraph>
                        <Paragraph>
                            Once deleted, <strong>this action cannot be undone</strong>, and there will be no way to recover any of your data or information. You will need to create a new profile to regain access to our services, but your previous data will not be retrievable.
                        </Paragraph>
                        <Paragraph>
                            If you are sure you want to permanently delete your profile, click "Confirm Delete." If you have any concerns, it is highly recommended that you back up any important data before proceeding. For further assistance, feel free to contact support.
                        </Paragraph>
                        <Paragraph>
                            Please note: Profile deletion does not remove any legal obligations or agreements you may have with the service, such as outstanding payments or responsibilities.
                        </Paragraph>

                        <div style={{ marginTop: '24px', textAlign: 'center' }}>
                            <Popconfirm
                                title="Are you absolutely sure you want to delete your profile?"
                                description="This action cannot be undone. All your data will be permanently deleted."
                                onConfirm={onDelete}
                                onCancel={onCancel}
                                okText="Yes, Delete"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true, loading: loader }}
                            >
                                <Button type="primary" danger size="large" loading={loader} disabled={loader}>
                                    Confirm Delete Profile
                                </Button>
                            </Popconfirm>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
};

export default DeleteProfile;

